import { assertEvent, assign } from 'xstate';

import { OrderTypeValues } from '@sg/shared-be-features-ordering';

import { clearLocalStorageOrderId, getLocalStorageOrderId } from '@order/features-ordering';
import { getElementConfig, getIntentConfig } from '@order/integrations-stripe';

import { logger } from './sweetpass-checkout.machine.logger';
import {
  type Action,
  type ConfirmPaymentResponse,
  type FetchPaymentInfoResponse,
  type FetchPendingPaymentResponse,
} from './sweetpass-checkout.machine.types';

// ─── Payment Setup Actions ───────────────────────────────────────────────────

const assignPaymentInfo: Action<FetchPaymentInfoResponse> = assign(({ context }, params) => {
  const { amount, stripeConfirmHandlerRef } = context;
  const { stripeCustomerId, stripeCustomerKey, stripeSession } = params;

  return {
    stripeSession,
    stripeIntentConfig: getIntentConfig({ amount, stripeConfirmHandlerRef }),
    stripeElementConfig: getElementConfig({
      returnURL: STRIPE_RETURN_URL,
      stripeCustomerId,
      stripeCustomerKey,
    }),
  };
});

const setConfirmStripe: Action = assign(({ event }) => {
  assertEvent(event, 'SET_CONFIRM_STRIPE');

  return { confirmStripe: event.params.confirmStripe };
});

const setStripeFailedToLoad: Action = assign({ stripeFailedToLoad: true });

const incrementPurchaseAttempt: Action = assign(({ context }) => {
  return { purchaseAttempt: context.purchaseAttempt + 1 };
});

// ─── Purchase Actions ────────────────────────────────────────────────────────

/**
 * Clears the pending payment stored on payment intent creation when Stripe
 * fails to confirm it, so the home screen doesn't redirect to the checkout.
 *
 * Pending payments of other order types are left alone, as the intent might
 * have failed before being created.
 */
const clearPendingPayment: Action = () => {
  void clearPendingSweetpassPayment();
};

// ─── Outcome Actions ─────────────────────────────────────────────────────────

const onPendingPaymentFound: Action<FetchPendingPaymentResponse> = () => {
  logger.warn('Please provide a {onPendingPaymentFound} action.');
};

const onPaymentConfirmed: Action<ConfirmPaymentResponse> = () => {
  logger.warn('Please provide a {onPaymentConfirmed} action.');
};

const onFailedToLoad: Action<{ errorMessage: string | undefined }> = () => {
  logger.warn('Please provide a {onFailedToLoad} action.');
};

const onAlreadyPurchased: Action = () => {
  logger.warn('Please provide a {onAlreadyPurchased} action.');
};

const onPaymentCanceled: Action = () => {
  logger.warn('Please provide a {onPaymentCanceled} action.');
};

const onPaymentFailed: Action<{ errorMessage: string | undefined }> = () => {
  logger.warn('Please provide a {onPaymentFailed} action.');
};

// ─────────────────────────────────────────────────────────────────────────────

export const Actions = {
  assignPaymentInfo,
  setConfirmStripe,
  setStripeFailedToLoad,
  incrementPurchaseAttempt,

  clearPendingPayment,

  onPendingPaymentFound,
  onPaymentConfirmed,
  onFailedToLoad,
  onAlreadyPurchased,
  onPaymentCanceled,
  onPaymentFailed,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function clearPendingSweetpassPayment() {
  const { orderType } = await getLocalStorageOrderId();

  if (orderType !== OrderTypeValues.SweetpassOrder) return;

  clearLocalStorageOrderId();
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STRIPE_RETURN_URL = 'sweetgreen-order://rewards/sweetpass-checkout';
